import { generateSetChild, generateUpdateArrayChild } from '@/helpers/VuexHelpers'
import { service, contaoService } from '@/service'
import { config } from '@/config'
import { dayjsLocal, isSameDay } from "@/helpers/dayjs"
import { switchDateFormat } from '@/utils/formatDate'
import { getGtmConsentCategories } from '@/helpers/TrackingHelpers'

export default {
	namespaced: true,
	state: {
		locations: [],
		lastSync: 0,
		restaurants: [],
		restaurant: null,
		seats: 2,
		room: null,
		availabilities: null,
		date: null,
		time: null,
		timeEnd: null,
		reservationHash: null,
		details: null,
		ical: null,
		activeRequests: 0,
		reservationEvents: [],
		monthAvailabilities: null
	},
	actions: {
		async loadReservationsList({ state, commit, rootState }, { accumulatorGroup = null } = {}) {
			if (Date.now() - 60000 < state.lastSync) {
				return "loaded cache"
			}

			try {
				state.activeRequests++;
				const result = await service.reservationChannelList({ brand: config.BRAND, accumulatorGroup })
				let fakeId = 0 // TODO: REMOVE THIS, only compatibilty
				// strip down result
				let locations = result.map(v => ({
					code: v.code,
					locationId: v.id || fakeId++,
					reservationUrl: v.reservationUrl,
					name: v.name,
					hasIndining: v.hasIndining
				}))
				commit('setLocations', { item: locations })
				state.lastSync = Date.now()
				state.activeRequests--;
			}
			catch(err) {
				state.activeRequests--;
				throw err;
			}
		},
		async loadRestaurants({ state, commit, rootState }, { accumulatorGroup = null } = {}) {
			try {
				state.activeRequests++;
				const result = await service.reservationRestaurants({ brand: config.BRAND, accumulatorGroup })
				let restaurants = result.map(v => ({
					code: v.code,
					name: v.name,
					indining: !!v.indining,
					indiningCutoff: v.indiningCutoff,
					brand: v.brand,
					image: v.images?.find(image => image.code == 'location_assortment_image')?.path || null,
					phoneNumber: v.phoneNumber,
					address: v.locationAddress,
					region: v.region,
					tags: v.tags || [],
				}))
				commit('setRestaurants', { item: restaurants })
				state.activeRequests--;
			}
			catch(err) {
				state.activeRequests--;
				throw err;
			}
		},
		setRestaurant({ commit, dispatch }, { channel } = {}) {
			commit('setRestaurant', { item: channel });
		},
		setSeats({ commit, dispatch }, { seats } = {}) {
			commit('setSeats', { item: seats });
		},
		async loadMonthAvailabilities({ state, commit, dispatch }, { from, to, accumulatorGroup = null } = {}) {
			if (!state.restaurant) {
				return
			}
			try {
				state.activeRequests++;
				const result = await service.reservationMonthAvailabilities({ brand: config.BRAND, channel: state.restaurant, seats: state.seats, from, to, accumulatorGroup })
				commit('setMonthAvailabilities', { item: result });
				state.activeRequests--;
			}
			catch(err) {
				commit('setMonthAvailabilities', { item: null });
				state.activeRequests--;
				throw err
			}
		},
		async loadAvailabilities({ state, commit, getters, dispatch }, { accumulatorGroup = null } = {}) {
			if (!getters.reservationDate) {
				return;
			}

			dispatch('loadReservationEvents', { code: state.restaurant, date: getters.reservationDate.split('.').reverse().join('-')});

			let startSec = Math.ceil((new Date(getters.reservationDate.split('.').reverse().join('-'))) / 1000);
			if (isSameDay(dayjsLocal(getters.reservationDate), dayjsLocal((new Date())))) {
				startSec = null;
			}
			try {
				state.activeRequests++;
				const result = await service.reservationAvailabilities({ brand: config.BRAND, channel: state.restaurant, seats: state.seats, startSec, accumulatorGroup })
				let room = null;
				if (Object.values(result.rooms).length === 1) {
					room = (Object.values(result.rooms).shift()).roomId;
				}
				if (state.room && Object.values(result.rooms).find(room => room.roomId == state.room)) {
					room = state.room;
				}
				else if (state.details?.roomId !== undefined) {
					room = state.details.roomId;
				}

				commit('setRoom', { item: room });
				commit('setAvailabilities', { item: result });

				const reservationTime = getters.reservationTimes?.find(time => time.time == getters.reservationTime);

				if (!reservationTime || (!reservationTime.available && state.details?.startSec !== reservationTime.availability)) {
					commit('setTime', { item: null });
				}
				state.activeRequests--;
			}
			catch(err) {
				commit('setAvailabilities', { item: null });
				state.activeRequests--;
				throw err
			}
		},
		async loadReservationEvents({ commit }, { code, date } = {}) {
			const result = await contaoService.getReservationEvents({ code, date });
			commit('setReservationEvents', { item: result?.events || [] });
		},
		setRoom({ commit }, { room } = {}) {
			commit('setRoom', { item: room });
		},
		setDate({ commit, dispatch }, { date } = {}) {
			commit('setDate', { item: date });
		},
		setTime({ commit }, { time } = {}) {
			commit('setTime', { item: time });
		},
		async createReservation({ state, commit, getters, rootState, dispatch }, { email, firstName, lastName, phoneNumber, notes, acceptTerms, acceptPrivacy, choices, indining, indiningAvailable, vegiSeats, accumulatorGroup = null } = {}) {
			const availability = getters.reservationAvailability;
			const room = getters.reservationRoom;
      const gtmConsent = getGtmConsentCategories()
			const result = await service.reservationCreate({ token: rootState.account.token, channel: state.restaurant, seats: state.seats, roomId: room.id, roomName: room.name, email, firstName, lastName, phoneNumber, notes, acceptTerms, acceptPrivacy, choices, indining, indiningAvailable, vegiSeats, startSec: availability.availability, editUrl: config.RESERVATION_EDIT_URL, gtmConsent, accumulatorGroup });
			commit('setDetails', { item: result });
			commit('setReservationHash', { item: result.reservationHash });

			if (result.duration) {
				const dateTime = dayjsLocal((new Date(result.startSec * 1000)));
				commit('setTimeEnd', { item: dateTime.add(result.duration, 'minute').format('HH:mm') });
			}

			await dispatch('loadReservationCalendar');
		},
		async loadReservation({ state, commit, rootState, dispatch }, { reservation, accumulatorGroup = null } = {}) {
			const result = await service.reservationDetails({ token: rootState.account.token, brand: config.BRAND, reservation, accumulatorGroup });
			commit('setDetails', { item: result });
			commit('setRestaurants', { item: [{ name: result.name, code: result.code }] })
			commit('setRestaurant', { item: result.code });
			commit('setRoom', { item: result.roomId });
			commit('setSeats', { item: result.seats });
			commit('setReservationHash', { item: result.reservationHash });

			const dateTime = dayjsLocal((new Date(result.startSec * 1000)));

			commit('setDate', { item: dateTime.format('L') });
			commit('setTime', { item: dateTime.format('HH:mm') });

			if (result.duration) {
				commit('setTimeEnd', { item: dateTime.add(result.duration, 'minute').format('HH:mm') });
			}
		},
		async editReservation({ state, commit, getters, rootState, dispatch }, { email, firstName, lastName, phoneNumber, notes, acceptTerms, acceptPrivacy, choices, indining, indiningAvailable, vegiSeats, accumulatorGroup = null } = {}) {
			const availability = getters.reservationAvailability;
			const room = getters.reservationRoom;
      const gtmConsent = getGtmConsentCategories()
			const result = await service.reservationEdit({ token: rootState.account.token, channel: state.restaurant, reservation: state.reservationHash, seats: state.seats, roomId: room.id, roomName: room.name, email, firstName, lastName, phoneNumber, notes, acceptTerms, acceptPrivacy, choices, indining, indiningAvailable, vegiSeats, startSec: availability.availability, editUrl: config.RESERVATION_EDIT_URL, gtmConsent, accumulatorGroup });
			commit('setReservationHash', { item: result.reservationHash });
			commit('setDetails', { item: result });

			if (result.duration) {
				const dateTime = dayjsLocal((new Date(result.startSec * 1000)));
				commit('setTimeEnd', { item: dateTime.add(result.duration, 'minute').format('HH:mm') });
			}

			await dispatch('loadReservationCalendar');
		},
		async loadReservationCalendar({ state, commit, rootState }, { accumulatorGroup = null } = {}) {
			const result = await service.reservationCalendar({ token: rootState.account.token, channel: state.restaurant, reservation: state.reservationHash, accumulatorGroup });
			commit('setIcal', { item: result });
		},
		async cancelReservation({ state, commit, getters, rootState, dispatch }, { reservation, accumulatorGroup = null } = {}) {
			const result = await service.reservationCancel({ token: rootState.account.token, reservation, accumulatorGroup });
			commit('setDetails', {
				item: {
					...state.details,
					status: 'canceled',
				}
			});
		},
		resetReservation({ commit }) {
			commit('setLocations', { item: [] });
			commit('setRestaurants', { item: [] });
			commit('setRestaurant', { item: null });
			commit('setSeats', { item: 2 });
			commit('setRoom', { item: null });
			commit('setDate', { item: null });
			commit('setTime', { item: null });
			commit('setTimeEnd', { item: null });
			commit('setReservationHash', { item: null });
			commit('setDetails', { item: null });
			commit('setAvailabilities', { item: null });
			commit('setIcal', { item: null });
		}
	},
	getters: {
		reservationLocations (state) {
			return state.locations
		},
		indiningLocations (state) {
			return state.locations.filter(location => location.hasIndining)
		},
		indiningRedirectUrl() {
			return config.INDINING_URL
		},
		reservationRestaurants(state) {
			let restaurants = state.restaurants
				.sort((a, b) => a.name.localeCompare(b.name) || a.brand.localeCompare(b.brand));

			restaurants = [
				...restaurants.filter(location => location.brand == config.BRAND),
				...restaurants.filter(location => location.brand != config.BRAND),
			]
			return restaurants;
		},
		reservationRestaurant(state, getters) {
			return getters.reservationRestaurants.find(r => r.code == state.restaurant) || {}
		},
		reservationRestaurantName(state, getters) {
			return getters.reservationRestaurant?.name || '';
		},
		reservationSeats(state) {
			return state.seats
		},
		reservationRooms(state) {
			return state.availabilities?.rooms
				? Object.values(state.availabilities.rooms).map(r => ({name: r.roomName, id: r.roomId, indining: r.indining}))
				: []
		},
		reservationRoom(state) {
			const room = state.availabilities?.rooms ? state.availabilities.rooms.find(room => room.roomId == state.room) : null;

			if (room) {
				return {
					name: room.roomName,
					id: room.roomId,
					choices: room.choices,
					indining: !!room.indining,
				}
			}

			return null;
		},
		reservationMonthAvailabilities(state) {
			return (state.monthAvailabilities || []).map(a => {
				return {
					...a,
					date: switchDateFormat(a.date, '.')
				}
			})
		},
		reservationDateAvailability(state, getters) {
			return (getters.reservationMonthAvailabilities || []).find(a => a.date === getters.reservationDate)
		},
		reservationOnlyByPhone(state, getters) {
			return getters.reservationDateAvailability?.reason === 'phone'
		},
		reservationAvailabilitiesLoaded(state) {
			return !!state.availabilities
		},
		reservationAvailabilities(state) {
			const room = state.availabilities?.rooms ? state.availabilities.rooms.find(room => room.roomId == state.room) : null;

			if (room) {
				return room.availabilities
					.sort((a, b) => a.availability - b.availability)
					.map(a => {
						const dayjs = dayjsLocal((new Date(a.availability * 1000)));
						let available = a.available;

						if (state.seats > a.max || state.seats < a.min) {
							available = false;
						}

						return {
							...a,
							dayjs,
							date: dayjs.format('L'),
							time: dayjs.format('LT'),
							available,
						}
					});
			}

			return [];
		},
		reservationAvailability(state, getters) {
			const availabilities =  getters.reservationAvailabilities;
			const date = getters.reservationDate;
			const time = getters.reservationTime;

			return availabilities.find(a => a.date == date && a.time == time);
		},
		reservationDate(state) {
			return state.date;
		},
		reservationTimes(state, getters) {
			const availabilities =  getters.reservationAvailabilities;
			const date = getters.reservationDate;
			const currentDate = dayjsLocal((new Date()));
			const today = currentDate.format('L');
			const now = currentDate.format('LT');

			if (date) {
				const times = Array.from(availabilities.reduce((t, a) => {
					if (
						a.date === date &&
						(
							(
								date === today &&
								a.time > now
							) ||
							date !== today
						)
					) {
						t.add(a);
					}

					return t;
				}, (new Set())));

				return times;
			}

			return [];
		},
		reservationTime(state) {
			return state.time;
		},
		reservationTimeEnd(state) {
			return state.timeEnd;
		},
		reservationChoices(state, getters) {
			const availability = getters.reservationAvailability;
			const room = getters.reservationRoom;

			if (availability && room?.choices) {
				return room.choices
					.filter(c => {
						return c.times.reduce((a, t) => {
							return a || availability.availability >= t.startSec && availability.availability <= t.endSec;
						}, false);
					})
					.map(c => {
						return {
							...c,
							info: {
								...c.info,
								options: Object.entries(c.info.options).map(([value, label]) => ({ value, label })),
								required: !!c.info.required && c.info.required !== 'optional'
							}
						}
					});
			}

			return []
		},
		reservationChoicesWithoutSpecials(state, getters) {
			return getters.reservationChoices.filter(c => !['vegiSeats', 'noIndining'].includes(c.info.label));
		},
		reservationVegiSeats(state, getters) {
			return !!getters.reservationChoices.find(c => c.info.label == 'vegiSeats');
		},
		reservationNoIndining(state, getters) {
			const time = getters.reservationTime
			const cutoff = getters.reservationRestaurant?.indiningCutoff

			if (time && cutoff) {
				return time >= cutoff
			}

			return !!getters.reservationChoices.find(c => c.info.label == 'noIndining');
		},
		reservationRules(state) {
			return state.availabilities?.rules || '';
		},
		reservationEvents(state) {
			return state.reservationEvents || [];
		},
		showReservationEvents(state, getters) {
			return getters.reservationRules.includes('[RESERVATION_EVENT]');
		},
		reservationHash(state) {
			return state.reservationHash;
		},
		reservationDetails(state) {
			if (state.details) {
				return {
					...state.details,
					choices: [state.details?.option], // HACK: until there is a better solution on the foratable api
					editable: state.details.status !== 'canceled' && state.details.cancelDeadlineAt > Math.ceil(Date.now() / 1000),
					canceled: state.details.status === 'canceled',
					pending: state.details.status === 'pending',
				};
			}
			return null;
		},
		reservationCalendar(state) {
			return state.ical || '';
		},
		reservationCalendarUrl(state, getters) {
			return `data:text/calendar;base64,${window.btoa(getters.reservationCalendar)}`;
		},
		reservationLoading(state) {
			return !!state.activeRequests;
		}
	},
	mutations: {
		setLocations: generateSetChild("locations"),
		setRestaurants: generateSetChild("restaurants"),
		setRestaurant: generateSetChild("restaurant"),
		setSeats: generateSetChild("seats"),
		setRoom: generateSetChild("room"),
		setDate: generateSetChild("date"),
		setTime: generateSetChild("time"),
		setTimeEnd: generateSetChild("timeEnd"),
		setReservationHash: generateSetChild("reservationHash"),
		setDetails: generateSetChild("details"),
		setAvailabilities: generateSetChild("availabilities"),
		setMonthAvailabilities: generateSetChild("monthAvailabilities"),
		setReservationEvents: generateSetChild("reservationEvents"),
		setIcal: generateSetChild("ical"),
	}
}
